/*
 * Übersichten (Dashboard-Konzept, Paket B): Konfiguration, Vorlagen und Datentypen der Bausteine.
 * Die Konfiguration enthält nie Kartendaten. Zahlen und Listen rechnet der Server bei jedem Abruf mit
 * den Rechten der betrachtenden Person (gemeinsamer Lesedienst `queryCards`).
 */
import type { CalColor, CalDates, CalLook, CalRange, CalShow } from './dashCalendar.js';
import { DUE_FILTER_LABEL, type DueFilter } from './filter.js';
import { PRIORITIES, type CustomField, type FieldType } from './types.js';

export const WIDGET_TYPES = ['deadlines', 'tasks', 'blocked', 'ready', 'agenda', 'calendar', 'boards', 'flow', 'distribution', 'trend', 'punctuality', 'workload', 'time', 'timeStats', 'activity', 'links', 'metric'] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];
export const WIDGET_SIZES = [4, 6, 12] as const;
export type WidgetSize = (typeof WIDGET_SIZES)[number];
export const DASH_DAYS = [7, 14, 30, 90, 180, 365] as const;
export const ROW_CHOICES = [5, 10, 20] as const;
export const MAX_WIDGETS = 20;
export const MAX_ROWS = 20;

/** Boardauswahl: alle zugänglichen, feste Auswahl oder dynamisch alle Boards eines Bereichs */
/** Boardauswahl: alle zugänglichen, feste Auswahl, dynamisch alle Boards eines Bereichs oder eines Teams (gewähren nie Rechte) */
export type DashBoards = { mode: 'all' } | { mode: 'selected'; ids: string[] } | { mode: 'area'; areaId: string } | { mode: 'team'; teamId: string };

/** Gemeinsame Filter einer Übersicht – dieselben Begriffe wie im Board-Filter */
export interface DashFilter {
  boards: DashBoards;
  /** 'me', 'none' (niemand zuständig) oder Personen-IDs; leer = alle */
  assignees: string[];
  priority: number[];
  due: DueFilter[];
  /** Zeitraum für Kennzahlen, Zeiten und Aktivität (Tage) */
  days: number;
  /** Bedingungen auf eigene Felder – je Board ausdrücklich zugeordnet (F02) */
  fields?: FieldCond[];
}

/* ---------- Eigene Felder über mehrere Boards (F02) ---------- */

export type FieldOp = 'is' | 'empty' | 'filled' | 'gte' | 'lte' | 'contains';
export const FIELD_OP_TEXT: Record<FieldOp, string> = { is: 'ist', empty: 'ist leer', filled: 'ist ausgefüllt', gte: 'mindestens', lte: 'höchstens', contains: 'enthält' };

/**
 * Eine Bedingung über gleichartige Felder verschiedener Boards. Gleichnamige Felder werden nie
 * automatisch zusammengelegt: Die Zuordnung je Board ist ausdrücklich (bei Auswahlfeldern auch die Option).
 * Karten aus Boards ohne Zuordnung zählen nicht mit.
 */
export interface FieldCond {
  label: string;
  op: FieldOp;
  /** Vergleichswert für Text, Zahl, Betrag, Datum (JJJJ-MM-TT) oder Ja/Nein */
  value: string | number | boolean | null;
  map: { boardId: string; fieldId: string; optionId?: string | null }[];
}

export type FieldKind = 'text' | 'number' | 'currency' | 'choice' | 'date' | 'checkbox' | 'person' | 'approval';
export function fieldKind(t: FieldType): FieldKind {
  if (t === 'number' || t === 'rating' || t === 'formula') return 'number';
  if (t === 'currency') return 'currency';
  if (t === 'select' || t === 'multiselect') return 'choice';
  if (t === 'date') return 'date';
  if (t === 'checkbox') return 'checkbox';
  if (t === 'person') return 'person';
  if (t === 'approval') return 'approval';
  return 'text';
}
export const FIELD_OPS_BY_KIND: Record<FieldKind, FieldOp[]> = {
  text: ['contains', 'is', 'filled', 'empty'],
  number: ['gte', 'lte', 'is', 'filled', 'empty'],
  currency: ['gte', 'lte', 'is', 'filled', 'empty'],
  choice: ['is', 'filled', 'empty'],
  date: ['gte', 'lte', 'is', 'filled', 'empty'],
  checkbox: ['is'],
  person: ['filled', 'empty'],
  /** 0.80: Freigabe – entschieden (freigegeben, teilweise, abgelehnt) oder offen */
  approval: ['filled', 'empty'],
};

/** Zuordnung prüfen: Felder vorhanden, im richtigen Board, gleichartig (z. B. nie Zahl mit Betrag) */
export function checkFieldCond(c: FieldCond, fields: Map<string, Pick<CustomField, 'id' | 'boardId' | 'name' | 'type' | 'options'>>): string | null {
  if (!c.map.length) return `„${c.label}“: keinem Board ist ein Feld zugeordnet.`;
  let kind: FieldKind | null = null;
  for (const m of c.map) {
    const f = fields.get(m.fieldId);
    if (!f || f.boardId !== m.boardId) return `„${c.label}“: Ein zugeordnetes Feld gibt es nicht mehr – bitte die Zuordnung reparieren.`;
    const k = fieldKind(f.type);
    if (kind && k !== kind) return `„${c.label}“: Die zugeordneten Felder sind nicht gleichartig (${kind} und ${k}). Bitte nur gleichartige Felder zuordnen.`;
    kind = k;
    if (k === 'choice' && c.op === 'is' && !f.options.some((o) => o.id === m.optionId)) return `„${c.label}“: Die gewählte Option in „${f.name}“ gibt es nicht mehr.`;
  }
  if (!FIELD_OPS_BY_KIND[kind!].includes(c.op)) return `„${c.label}“: Dieser Vergleich passt nicht zum Feldtyp.`;
  return null;
}

const emptyVal = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/** Erfüllt eine Karte die Bedingung? Karten aus Boards ohne Zuordnung: nein */
export function fieldCondMatches(c: FieldCond, card: { boardId: string; custom: Record<string, unknown> }, fields: Map<string, Pick<CustomField, 'type'>>): boolean {
  const m = c.map.find((x) => x.boardId === card.boardId);
  if (!m) return false;
  const f = fields.get(m.fieldId);
  if (!f) return false;
  const v = card.custom[m.fieldId];
  if (c.op === 'empty') return emptyVal(v);
  if (c.op === 'filled') return !emptyVal(v);
  const k = fieldKind(f.type);
  if (k === 'choice') return Array.isArray(v) ? v.includes(m.optionId) : v === m.optionId;
  if (k === 'checkbox') return !!v === !!c.value;
  if (emptyVal(v)) return false;
  if (k === 'number' || k === 'currency') {
    const a = Number(v);
    const b = Number(c.value);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    return c.op === 'gte' ? a >= b : c.op === 'lte' ? a <= b : a === b;
  }
  if (k === 'date') {
    const a = String(v).slice(0, 10);
    const b = String(c.value ?? '').slice(0, 10);
    return c.op === 'gte' ? a >= b : c.op === 'lte' ? a <= b : a === b;
  }
  const a = String(v).toLowerCase();
  const b = String(c.value ?? '').toLowerCase();
  return c.op === 'contains' ? a.includes(b) : a === b;
}

export const DEFAULT_DASH_FILTER: DashFilter = { boards: { mode: 'all' }, assignees: [], priority: [], due: [], days: 30 };

/** 0.87: Aufteilung im Baustein „Verteilung“ */
export const DIST_BY = ['stage', 'column', 'assignee', 'label', 'priority', 'board', 'due'] as const;
export type DistBy = (typeof DIST_BY)[number];
export const DIST_BY_TEXT: Record<DistBy, string> = {
  stage: 'Arbeitsstand (nicht begonnen, in Arbeit, erledigt)',
  column: 'Spalte (gleichnamige Spalten zusammen)',
  assignee: 'Zuständige Person',
  label: 'Label (gleichnamige Labels zusammen)',
  priority: 'Priorität',
  board: 'Board',
  due: 'Fristlage (überfällig, heute, 7 Tage, später, ohne)',
};

export interface WidgetConfigs {
  deadlines: Record<string, never>;
  tasks: { rows: number; sort: 'due' | 'priority' | 'updated' };
  blocked: { rows: number };
  ready: { rows: number };
  agenda: { days: 7 | 14 | 30; rows: number };
  boards: { source: 'favorites' | 'recent' | 'selected'; boardIds: string[]; rows: number };
  flow: Record<string, never>;
  time: Record<string, never>;
  activity: { rows: number };
  links: { items: { title: string; url: string }[] };
  /** 0.87: Wochen-/Monatsbaustein */
  calendar: {
    range: CalRange;
    dates: CalDates;
    look: CalLook;
    show: CalShow[];
    color: CalColor;
    /** erledigte Karten durchgestrichen zeigen */
    done: boolean;
    /** Zeitraster von/bis (volle Stunden) */
    hours: [number, number];
  };
  /** 0.87: Verteilung offener (oder aller) Karten */
  distribution: { by: DistBy; chart: 'bars' | 'donut'; cards: 'open' | 'all'; rows: number };
  /** 0.87: Neu angelegt vs. erledigt je Woche/Monat, dazu Bestand */
  trend: { interval: 'week' | 'month'; stock: boolean };
  /** 0.87: Termintreue erledigter Karten */
  punctuality: { interval: 'week' | 'month'; groupBy: 'none' | 'assignee' | 'board' };
  /** 0.87: Auslastung je Person */
  workload: { measure: 'cards' | 'estimate'; unassigned: boolean; rows: number };
  /** 0.87: Gebuchte Zeit (eigene oder aller) nach Woche, Board, Person oder Karte */
  timeStats: { people: 'me' | 'all'; groupBy: 'week' | 'board' | 'person' | 'card' };
  /** 0.82: Kennzahl – Auswertung eines Zahlenfeldes je Board ausdrücklich zugeordnet (in Vorlagen boardId „board“) */
  metric: {
    agg: 'sum' | 'avg' | 'min' | 'max' | 'count';
    /** 0.84: `labelIds` – nur Karten mit mindestens einem dieser Labels (je Board, leer/fehlt = alle) */
    map: { boardId: string; fieldId: string; labelIds?: string[] }[];
    groupBy: 'none' | 'column' | 'assignee' | 'label' | 'month';
    /** nur offene Karten oder alle (inkl. erledigte, ohne Archiv) */
    cards: 'open' | 'all';
    /** Zielwert (Plan) – Balken Ist/Plan */
    target: number | null;
    /** 0.86: Zielrichtung – neutral (Standard), erreichen (≥ 100 % gut), Obergrenze (> 100 % rot) */
    targetMode?: 'neutral' | 'reach' | 'limit';
  };
}

export type Widget = {
  [T in WidgetType]: {
    id: string;
    type: T;
    size: WidgetSize;
    /** eigener Titel, sonst der Standardtitel des Bausteins */
    title: string | null;
    /** Abweichende Filter (nur die gesetzten Schlüssel weichen ab) */
    filter: Partial<DashFilter>;
    config: WidgetConfigs[T];
  };
}[WidgetType];

export type DashboardScope = 'personal' | 'team' | 'board';

export interface Dashboard {
  id: string;
  scope: DashboardScope;
  title: string;
  version: number;
  filter: DashFilter;
  widgets: Widget[];
  updatedAt: string;
  /** darf die betrachtende Person die Konfiguration ändern? */
  canEdit: boolean;
  teamId: string | null;
  teamName: string | null;
  boardId: string | null;
}

export interface DashboardListItem {
  id: string;
  scope: DashboardScope;
  title: string;
  teamId: string | null;
  teamName: string | null;
}

export interface DashboardPrefs {
  /** persönliche Startseite (Übersicht statt Boardliste) */
  startDashboardId: string | null;
  lastDashboardId: string | null;
  /** Eigene Reihenfolge am Handy je Übersicht (Baustein-IDs) */
  mobileOrder: Record<string, string[]>;
}

/** Bausteine in der Reihenfolge fürs Handy: eigene Reihenfolge zuerst, neue Bausteine hinten */
export function orderForPhone<T extends { id: string }>(widgets: T[], order: string[] | undefined): T[] {
  if (!order?.length) return widgets;
  const pos = new Map(order.map((id, i) => [id, i]));
  return [...widgets].sort((a, b) => (pos.get(a.id) ?? 1e6 + widgets.indexOf(a)) - (pos.get(b.id) ?? 1e6 + widgets.indexOf(b)));
}

export const WIDGET_INFO: Record<WidgetType, { title: string; text: string; size: WidgetSize; uses: (keyof DashFilter)[] }> = {
  deadlines: { title: 'Fristen', text: 'Überfällig, heute, nächste 7 Tage und ohne Frist', size: 12, uses: ['boards', 'assignees', 'priority', 'fields'] },
  tasks: { title: 'Als Nächstes', text: 'Offene Aufgaben nach Frist oder Priorität', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
  blocked: { title: 'Blockaden', text: 'Manuell blockiert und wartend auf Vorgänger', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
  ready: { title: 'Startbereit', text: 'Noch nicht begonnen, nicht blockiert, Vorgänger erledigt', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
  agenda: { title: 'Agenda', text: 'Anstehende Starts und Fristen', size: 6, uses: ['boards', 'assignees', 'priority', 'fields'] },
  boards: { title: 'Meine Boards', text: 'Favoriten, zuletzt geöffnet oder ausgewählte Boards', size: 6, uses: ['assignees', 'priority'] },
  flow: { title: 'Ablauf', text: 'Erledigt je Woche, Durchlaufzeit, Alter offener Karten', size: 12, uses: ['boards', 'assignees', 'priority', 'days', 'fields'] },
  time: { title: 'Meine Zeiten', text: 'Selbst gebuchte Zeit im Zeitraum', size: 4, uses: ['boards', 'days'] },
  activity: { title: 'Neu und geändert', text: 'Letzte Änderungen an Karten, je Karte gebündelt', size: 6, uses: ['boards', 'assignees', 'priority', 'days', 'fields'] },
  calendar: { title: 'Kalender', text: 'Woche oder Monat mit Aufgaben – als Streifen oder Flächen mit Uhrzeit, Umplanen per Ziehen', size: 12, uses: ['boards', 'assignees', 'priority', 'fields'] },
  distribution: { title: 'Verteilung', text: 'Karten je Spalte, Arbeitsstand, Person, Label, Priorität, Board oder Fristlage', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
  trend: { title: 'Neu und erledigt', text: 'Angelegte und erledigte Karten je Woche oder Monat, dazu der offene Bestand', size: 6, uses: ['boards', 'assignees', 'priority', 'days', 'fields'] },
  punctuality: { title: 'Termintreue', text: 'Pünktlich und verspätet erledigte Karten, Verspätung im Mittel', size: 6, uses: ['boards', 'assignees', 'priority', 'days', 'fields'] },
  workload: { title: 'Auslastung', text: 'Offene Karten oder geschätzter Aufwand je Person – begonnen, noch nicht begonnen, überfällig', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
  timeStats: { title: 'Zeiterfassung', text: 'Gebuchte Zeit nach Woche, Board, Person oder Karte; Schätzung und Ist', size: 6, uses: ['boards', 'days'] },
  links: { title: 'Wichtige Verknüpfungen', text: 'Ausgewählte Seiten in KaBa und sichere externe Links', size: 4, uses: [] },
  metric: { title: 'Kennzahl', text: 'Summe, Durchschnitt oder Anzahl eines Feldes – optional mit Zielwert und Aufteilung', size: 6, uses: ['boards', 'assignees', 'priority', 'due', 'fields'] },
};

export function defaultConfig<T extends WidgetType>(type: T): WidgetConfigs[T] {
  const c: { [K in WidgetType]: WidgetConfigs[K] } = {
    deadlines: {},
    tasks: { rows: 5, sort: 'due' },
    blocked: { rows: 5 },
    ready: { rows: 5 },
    agenda: { days: 14, rows: 10 },
    boards: { source: 'favorites', boardIds: [], rows: 5 },
    flow: {},
    time: {},
    activity: { rows: 10 },
    links: { items: [] },
    metric: { agg: 'sum', map: [], groupBy: 'none', cards: 'all', target: null },
    calendar: { range: 'week', dates: 'due', look: 'strips', show: ['time', 'board'], color: 'status', done: false, hours: [7, 19] },
    distribution: { by: 'stage', chart: 'bars', cards: 'open', rows: 10 },
    trend: { interval: 'week', stock: true },
    punctuality: { interval: 'week', groupBy: 'none' },
    workload: { measure: 'cards', unassigned: true, rows: 10 },
    timeStats: { people: 'me', groupBy: 'week' },
  };
  return c[type];
}

/** Wirksamer Filter eines Bausteins: abweichende Schlüssel überschreiben die Übersicht */
export function effectiveFilter(dash: DashFilter, w: Pick<Widget, 'filter'>): DashFilter {
  return { ...dash, ...w.filter };
}

export const widgetTitle = (w: Pick<Widget, 'type' | 'title'>) => w.title?.trim() || WIDGET_INFO[w.type].title;

/* ---------- Vorlagen ---------- */

export const DASHBOARD_TEMPLATES = {
  arbeitstag: { title: 'Mein Arbeitstag', text: 'Fristen, nächste Aufgaben, Blockaden und Favoriten – für dich.' },
  team: { title: 'Teamsteuerung', text: 'Fristen, Aufgaben ohne Zuständige, Blockaden, Boards und Durchsatz.' },
  veranstaltung: { title: 'Veranstaltung', text: 'Startbereite Aufgaben, Termine, offene Punkte und Verknüpfungen.' },
  planung: { title: 'Planung und Auswertung', text: 'Wochenkalender mit Uhrzeiten, Arbeitsstand, neu und erledigt, Termintreue und Auslastung.' },
  leer: { title: 'Leere Übersicht', text: 'Ohne Bausteine – du stellst sie selbst zusammen.' },
} as const;
export type DashboardTemplate = keyof typeof DASHBOARD_TEMPLATES;

/** Konfiguration aus einer Vorlage (nur Einstellungen, keine Daten) */
export function dashboardFromTemplate(key: DashboardTemplate, newId: () => string, ctx: { teamId?: string } = {}): { title: string; filter: DashFilter; widgets: Widget[] } {
  const d = templateConfig(key, newId);
  // Teamübersicht: dynamisch die Boards des Teams (jede Person sieht davon nur ihre)
  return ctx.teamId ? { ...d, filter: { ...d.filter, boards: { mode: 'team', teamId: ctx.teamId } } } : d;
}

/** Boardübersicht: ein Board, Bausteine ohne Boardauswahl */
export function boardDashboardConfig(newId: () => string): { title: string; filter: DashFilter; widgets: Widget[] } {
  const w = <T extends WidgetType>(type: T, size: WidgetSize, over: Partial<WidgetConfigs[T]> = {}, title: string | null = null): Widget =>
    ({ id: newId(), type, size, title, filter: {}, config: { ...defaultConfig(type), ...over } }) as Widget;
  return {
    title: 'Boardübersicht',
    filter: { ...DEFAULT_DASH_FILTER },
    widgets: [w('deadlines', 12), w('tasks', 6, { rows: 10, sort: 'priority' }), w('blocked', 6), w('ready', 6), w('agenda', 6, { days: 14, rows: 10 }), w('flow', 12), w('activity', 6)],
  };
}

function templateConfig(key: DashboardTemplate, newId: () => string): { title: string; filter: DashFilter; widgets: Widget[] } {
  const w = <T extends WidgetType>(type: T, size: WidgetSize, over: Partial<WidgetConfigs[T]> = {}, filter: Partial<DashFilter> = {}, title: string | null = null): Widget =>
    ({ id: newId(), type, size, title, filter, config: { ...defaultConfig(type), ...over } }) as Widget;
  const title = DASHBOARD_TEMPLATES[key].title;
  switch (key) {
    case 'arbeitstag':
      return {
        title,
        filter: { ...DEFAULT_DASH_FILTER, assignees: ['me'] },
        widgets: [
          w('deadlines', 12),
          w('tasks', 6, { rows: 10, sort: 'due' }),
          w('boards', 6, { source: 'favorites' }, { assignees: [] }),
          w('blocked', 6),
          w('ready', 6),
        ],
      };
    case 'team':
      return {
        title,
        filter: { ...DEFAULT_DASH_FILTER, days: 90 },
        widgets: [
          w('deadlines', 12),
          w('tasks', 6, { rows: 10, sort: 'priority' }, { assignees: ['none'] }, 'Ohne Zuständige'),
          w('blocked', 6),
          w('boards', 6, { source: 'recent' }),
          w('flow', 6),
        ],
      };
    case 'veranstaltung':
      return {
        title,
        filter: { ...DEFAULT_DASH_FILTER },
        widgets: [w('ready', 6, { rows: 10 }), w('agenda', 6, { days: 30, rows: 10 }), w('boards', 6, { source: 'favorites' }, {}, 'Offene Punkte nach Board'), w('blocked', 6), w('links', 6)],
      };
    case 'planung':
      return {
        title,
        filter: { ...DEFAULT_DASH_FILTER, days: 90 },
        widgets: [
          w('calendar', 12, { look: 'blocks', dates: 'span', show: ['time', 'board', 'assignees'], color: 'board' }, {}, 'Diese Woche'),
          w('distribution', 6, { by: 'stage', chart: 'donut', cards: 'open' }, {}, 'Arbeitsstand'),
          w('workload', 6),
          w('trend', 6),
          w('punctuality', 6),
        ],
      };
    case 'leer':
      return { title, filter: { ...DEFAULT_DASH_FILTER }, widgets: [] };
  }
}

/* ---------- Daten der Bausteine ---------- */

/** Karte in einer Bausteinliste (nur Felder, die die Person ohnehin sehen darf) */
export interface DashCard {
  id: string;
  boardId: string;
  boardTitle: string;
  columnTitle: string;
  title: string;
  dueAt: string | null;
  dueHasTime: boolean;
  startAt: string | null;
  startHasTime: boolean;
  priority: number;
  blocked: boolean;
  assigneeIds: string[];
}

/** Mengen, die sich als vollständige Trefferliste öffnen lassen (gleiche Rechnung wie die Zahl, A04) */
export type DashSet = 'overdue' | 'today' | 'week' | 'none' | 'open' | 'blocked' | 'waiting' | 'ready';
export const DASH_SET_TEXT: Record<DashSet, string> = {
  overdue: 'Überfällig',
  today: 'Heute fällig',
  week: 'Nächste 7 Tage',
  none: 'Ohne Frist',
  open: 'Offene Aufgaben',
  blocked: 'Manuell blockiert',
  waiting: 'Warten auf Vorgänger',
  ready: 'Startbereit',
};

export type WidgetData =
  | { type: 'deadlines'; overdue: number; today: number; week: number; none: number; asOf: string; timeZone: string }
  | { type: 'tasks'; total: number; items: DashCard[] }
  | {
      type: 'blocked';
      manualTotal: number;
      waitingTotal: number;
      manual: { card: DashCard; sinceDays: number | null; reason: string | null }[];
      waiting: { card: DashCard; open: { id: string; title: string }[] }[];
    }
  | { type: 'ready'; total: number; items: { card: DashCard; handoverAt: string | null }[] }
  | { type: 'agenda'; cards: number; events: { card: DashCard; kind: 'start' | 'due'; at: string; hasTime: boolean }[]; more: number }
  | { type: 'boards'; items: { id: string; title: string; open: number; blocked: number; overdue: number; nextDue: { at: string; hasTime: boolean } | null }[] }
  | {
      type: 'flow';
      completed: number;
      reopened: number;
      throughput: { weekStart: string; label: string; count: number | null; partial: boolean }[];
      cycle: { median: number | null; p85: number | null; count: number; unknown: number };
      openAge: { median: number | null; count: number };
      wip: number;
      boards: number;
    }
  | { type: 'time'; minutes: number; running: boolean; byBoard: { boardId: string; title: string; minutes: number }[]; days: number }
  | {
      type: 'activity';
      items: { card: DashCard; lastAt: string; count: number; kinds: string[]; actorIds: string[] }[];
      people: Record<string, string>;
    }
  | { type: 'links' }
  /** 0.87: Kalender – Karten mit Terminen im Zeitraum (± 1 Tag Puffer für Zeitzonen) */
  | {
      type: 'calendar';
      from: string;
      to: string;
      anchor: string;
      cards: CalCard[];
      /** weitere Karten im Zeitraum über der Höchstzahl */
      more: number;
      boards: { id: string; title: string; canEdit: boolean }[];
      labels: { id: string; name: string; color: string }[];
      people: Record<string, string>;
    }
  | { type: 'distribution'; by: DistBy; total: number; groups: StatGroup[]; other: number; overlap: boolean }
  | {
      type: 'trend';
      interval: 'week' | 'month';
      buckets: { start: string; label: string; created: number; completed: number; open: number; partial: boolean }[];
      created: number;
      completed: number;
    }
  | {
      type: 'punctuality';
      interval: 'week' | 'month';
      onTime: number;
      late: number;
      /** erledigt im Zeitraum, aber ohne Frist (zählen nicht mit) */
      noDue: number;
      /** Verspätung verspäteter Karten in Tagen (Median) */
      lateMedian: number | null;
      /** jetzt offen und überfällig */
      overdueOpen: number;
      buckets: { start: string; label: string; onTime: number; late: number; partial: boolean }[];
      groups: { key: string; label: string; onTime: number; late: number }[];
    }
  | {
      type: 'workload';
      measure: 'cards' | 'estimate';
      people: { key: string; label: string; open: number; started: number; overdue: number; estimate: number; withoutEstimate: number }[];
      other: number;
      overlap: boolean;
    }
  | {
      type: 'timeStats';
      people: 'me' | 'all';
      groupBy: 'week' | 'board' | 'person' | 'card';
      minutes: number;
      running: boolean;
      days: number;
      groups: { key: string; label: string; minutes: number; partial?: boolean }[];
      /** Erledigte Karten im Zeitraum mit Schätzung: geschätzt vs. gebucht (alle Personen) */
      estimate: { cards: number; estimated: number; spent: number } | null;
    }
  /** 0.82: Kennzahl; `field` für Einheit/Währung der Anzeige */
  | {
      type: 'metric';
      value: number | null;
      n: number;
      field: { name: string; type: FieldType; config: CustomField['config'] } | null;
      groups: { label: string; value: number | null; n: number }[];
      target: number | null;
      /** 0.84: Namen der Labels, auf die eingeschränkt ist (leer = keine Einschränkung) */
      onlyLabels?: string[];
      /** 0.86: Karten in der Auswahl (mit und ohne Wert) – Bezugsmenge für „2 von 3 mit Wert“ */
      cards?: number;
      /** 0.86: Karten zählen in mehreren Gruppen (Labels, Zuständige) – Gruppen nicht addierbar */
      overlap?: boolean;
    };

/** 0.87: Karte im Kalenderbaustein – mit Version (Umplanen ohne Überschreiben) und Erledigt-Zeitpunkt */
export interface CalCard extends DashCard {
  version: number;
  completedAt: string | null;
  labelIds: string[];
}

/** Persönliche Einblendung, bewusst kein Bestandteil von WidgetData oder Übersichtsexporten. */
export interface CalendarPlanningData {
  blocks: { id: string; start: string; end: string; state: 'planned' | 'performed'; card: CalCard }[];
  more: number;
  boards: { id: string; title: string; canEdit: boolean }[];
  labels: { id: string; name: string; color: string }[];
  people: Record<string, string>;
}

/** 0.87: Gruppe einer Statistik; `key` öffnet die Trefferliste (gleiche Rechnung, A04) */
export interface StatGroup {
  key: string;
  label: string;
  count: number;
  /** Labelfarbe (bei Labels) */
  color?: string | null;
}

export type WidgetResult = { id: string; status: 'ok'; data: WidgetData } | { id: string; status: 'error'; message: string };

export interface DashboardDataResponse {
  generatedAt: string;
  timeZone: string;
  /** Anzahl der Boards, aus denen Daten stammen (keine Namen fremder Boards) */
  boards: number;
  results: WidgetResult[];
}

/** Sichere Ziele für „Wichtige Verknüpfungen“: interne Pfade oder https-Adressen */
export function safeLink(url: string): { href: string; external: boolean } | null {
  const u = url.trim();
  if (/^\/(?!\/)[^\s]*$/.test(u)) return { href: u, external: false };
  if (/^https:\/\/[^\s/]+[^\s]*$/i.test(u)) return { href: u, external: true };
  return null;
}

/** Wirksame Filter als kurze Chips am Baustein; `own` = weicht von der Übersicht ab (F01) */
export function widgetFilterChips(
  dash: DashFilter,
  w: Pick<Widget, 'type' | 'filter'>,
  areaName?: (id: string) => string | undefined,
  opts: { boardScope?: boolean } = {},
): { label: string; own: boolean }[] {
  const f = effectiveFilter(dash, w);
  // Boardübersicht: die Boardauswahl steht fest und wird nicht als Chip wiederholt
  const uses = WIDGET_INFO[w.type].uses.filter((k) => !opts.boardScope || k !== 'boards');
  const out: { label: string; own: boolean }[] = [];
  const own = (k: keyof DashFilter) => k in w.filter;
  if (uses.includes('boards') && (f.boards.mode !== 'all' || own('boards')))
    out.push({
      label:
        f.boards.mode === 'all'
          ? 'Alle Boards'
          : f.boards.mode === 'team'
            ? 'Boards des Teams'
            : f.boards.mode === 'selected'
              ? f.boards.ids.length === 1
                ? '1 Board'
                : `${f.boards.ids.length} Boards`
              : `Bereich ${areaName?.(f.boards.areaId) ?? ''}`.trim(),
      own: own('boards'),
    });
  if (uses.includes('assignees') && (f.assignees.length || own('assignees')))
    out.push({
      label: !f.assignees.length
        ? 'Alle Zuständigen'
        : f.assignees.length === 1 && f.assignees[0] === 'me'
          ? 'Mir zugewiesen'
          : f.assignees.length === 1 && f.assignees[0] === 'none'
            ? 'Ohne Zuständige'
            : f.assignees.length === 1 ? '1 Person' : `${f.assignees.length} Personen`,
      own: own('assignees'),
    });
  if (uses.includes('priority') && (f.priority.length || own('priority')))
    out.push({ label: f.priority.length ? `Priorität: ${f.priority.map((p) => PRIORITIES.find((x) => x.value === p)?.label ?? p).join(', ')}` : 'Jede Priorität', own: own('priority') });
  if (uses.includes('due') && (f.due.length || own('due')))
    out.push({ label: f.due.length ? `Frist: ${f.due.map((d) => DUE_FILTER_LABEL[d]).join(', ')}` : 'Jede Frist', own: own('due') });
  if (uses.includes('fields')) for (const c of f.fields ?? []) out.push({ label: `${c.label} ${FIELD_OP_TEXT[c.op]}${c.op === 'is' || c.op === 'gte' || c.op === 'lte' || c.op === 'contains' ? (c.value === null || c.value === undefined || c.value === '' ? '' : ` ${c.value === true ? 'ja' : c.value === false ? 'nein' : c.value}`) : ''} (${c.map.length} ${c.map.length === 1 ? 'Board' : 'Boards'})`, own: own('fields') });
  if (uses.includes('days')) out.push({ label: f.days === 365 ? 'Letztes Jahr' : `Letzte ${f.days} Tage`, own: own('days') });
  return out;
}
